import React, { useState } from 'react';
import { useMutation } from 'react-query';
import { Path, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { unimpersonateUser } from 'api/user';
import pages, { PageKey } from 'pages';
import { useUser } from 'hooks/useUser';
import { useBreakpoints } from 'hooks/useWindowSize';
import {
  AbsoluteRight,
  AlertsAccountWrapper,
  BreadCrumbs,
  HeaderStyled,
  LinkStyled,
  LogoutText,
  Title,
  TitleBold,
  TitleButtonsWrapper,
  TitleWrapper,
} from './Header.styles';
import { AccountMenu } from '../AccountMenu/AccountMenu';
import { Modal } from '../Modal/Modal';
import { toast } from '../Toast/Toast';
import { Text } from '../Text/Text';
import { Button } from '../Button/Button';
import { AccountIcon } from '../icons/AccountIcon';
import { FoldersIcon } from '../icons/FoldersIcon';
import { MessageCenterModal } from '../MessageCenter/MessageCenterModal/MessageCenterModal';
import { BannerProps } from '../Banner/Banner';

type Props = {
  pageName: PageKey;
  title?: string;
  routeBreadCrumbs?: { label: string; link?: Path }[];
  canArchiveUser?: boolean;
  onImpersonateUser?: () => void;
  onChangeArchive?: () => void;
  archived?: boolean;
  banner?: React.ReactNode;
};

export function Header({
  pageName,
  title,
  routeBreadCrumbs,
  onImpersonateUser,
  onChangeArchive,
  archived,
  canArchiveUser = true,
  banner = null,
}: Props) {
  const [openLogoutModal, setOpenLogoutModal] = useState(false);
  const [isBannerVisible, setIsBannerVisible] = useState(false);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const breakpoints = useBreakpoints();
  const { logout, userResult, setUserResult } = useUser();

  const toggleModal = () => setOpenLogoutModal(!openLogoutModal);

  const { mutate: unimpersonateUserMutation } = useMutation({
    mutationFn: unimpersonateUser,
    onSuccess: ({ data }) => {
      const newUser = data.user;
      const newImpersonator = data?.impersonator;
      const impersonatedUserId = userResult?.user?.id;

      setUserResult({
        user: newUser,
        impersonator: newImpersonator,
        selectedAgreement: newUser.agreements[0],
      });

      navigate(`/${pages.ADMIN}/users/${impersonatedUserId}`);
    },
    onError: () => {
      toast({
        type: 'error',
        title: t('toast.error'),
        message: t('toast.somethingWentWrong'),
        theme,
      });
    },
  });

  const handleLogout = () => {
    logout({});
  };

  const bannerWithCallback =
    banner && React.isValidElement(banner)
      ? React.cloneElement(banner as React.ReactElement<BannerProps>, {
          onVisibilityChange: setIsBannerVisible,
        })
      : banner;

  const name = userResult?.user?.first_name || '';
  let headerTitle = title ? <Title as="h1">{title}</Title> : null;
  let breadCrumbs: JSX.Element[] | null = null;

  if (pageName === 'OVERVIEW' || pageName === 'STATUS') {
    headerTitle = (
      <>
        <Title as="h1">{title}</Title> <TitleBold as="h1">{name}</TitleBold>
      </>
    );
  }

  if (
    pageName === 'ACCOUNT' ||
    pageName === 'AGREEMENT' ||
    pageName === 'SYSTEM' ||
    pageName === 'HARDWARE_INFO' ||
    pageName === 'AGREEMENTS' ||
    pageName === 'USER' ||
    pageName === 'MANAGE_FAQS' ||
    pageName === 'VPP_DETAILS'
  ) {
    headerTitle = <TitleBold as="h1">{title}</TitleBold>;
  }

  const accountIcon = () => (
    <AccountIcon color={theme.colors.iconIconPrimaryPressedSecondary2100} filled={false} />
  );

  if (pageName === 'USER') {
    headerTitle = (
      <TitleButtonsWrapper>
        <TitleBold as="h1">{title}</TitleBold>
        {!archived && (
          <Button
            label="Impersonate"
            styleVariant="secondary"
            Icon={accountIcon}
            onClick={onImpersonateUser}
          />
        )}
        {canArchiveUser && (
          <AbsoluteRight>
            <Button
              label={`${archived ? 'Unarchive' : 'Archive'} User`}
              styleVariant="tertiary"
              Icon={FoldersIcon}
              onClick={onChangeArchive}
            />
          </AbsoluteRight>
        )}
      </TitleButtonsWrapper>
    );
  }

  if (routeBreadCrumbs) {
    breadCrumbs = routeBreadCrumbs.map((path) => {
      const showEllipsis = routeBreadCrumbs.length > 2 && breakpoints === 'sm';

      if (path.link) {
        return (
          <LinkStyled key={path.label} to={path.link}>
            {showEllipsis ? '...' : path.label} /
          </LinkStyled>
        );
      }

      return (
        <Text key={path.label} as="p">
          {path.label}
        </Text>
      );
    });
  }

  return (
    <HeaderStyled
      $hasBreadCrumbs={Boolean(breadCrumbs)}
      $hasTitle={Boolean(headerTitle)}
      $hasBanner={isBannerVisible}
    >
      <AlertsAccountWrapper>
        <MessageCenterModal />
        <AccountMenu
          name={name}
          title={t('account.account')}
          onUnImpersonateUser={userResult?.impersonator ? unimpersonateUserMutation : undefined}
          onLogout={toggleModal}
        />
      </AlertsAccountWrapper>

      <TitleWrapper>
        {bannerWithCallback}
        <div>
          {breadCrumbs && <BreadCrumbs $hasTitle={Boolean(headerTitle)}>{breadCrumbs}</BreadCrumbs>}
          {headerTitle}
        </div>
      </TitleWrapper>

      <Modal
        isOpen={openLogoutModal}
        contentLabel="Log Out Modal"
        title={t('logoutModal.logout')}
        styleVariant="tertiary"
        isFullWidth
        zIndex={openLogoutModal ? 1002 : undefined}
        onRequestClose={toggleModal}
        ctaButtons={{
          tertiary: { label: t('cta.cancel'), onClick: toggleModal },
          primary: { label: t('logoutModal.logout'), onClick: handleLogout },
        }}
      >
        <LogoutText as="p">{t('logoutModal.areYouSure')}</LogoutText>
      </Modal>
    </HeaderStyled>
  );
}

export default Header;
